import { useEffect, useMemo } from 'react';
import type { PhaseCode } from '../../../components/menu.config';
import { PhaseTaskList } from '../components/PhaseTaskList';
import { PhaseTimeline } from '../components/PhaseTimeline';
import { useAdvanceUser, useCatalog, useMySites, useSiteProgress } from '../hooks/useProgression';
import { useSelectedPhaseStore } from '../store/selected-phase.store';
import { useSiteSelectionStore } from '../store/site-selection.store';

export function CertificationJourneyPage() {
  const { selectedSiteId, setSelectedSiteId } = useSiteSelectionStore();
  const selectedPhaseCodeFromStore = useSelectedPhaseStore((s) => s.selectedPhaseCode);
  const setSelectedPhaseCode = useSelectedPhaseStore((s) => s.setSelectedPhaseCode);

  const catalog = useCatalog();
  const sites = useMySites();

  const assignedSiteIds = sites.data?.map((s) => s.siteId) ?? null;
  const selectionIsValid =
    Boolean(selectedSiteId) && Boolean(assignedSiteIds?.includes(selectedSiteId as string));

  /** Ne pas charger la progression avec un UUID persisté (localStorage) obsolète — évite 403 et boucles. */
  const progress = useSiteProgress(selectionIsValid ? selectedSiteId : null);

  useEffect(() => {
    const list = sites.data;
    if (list === undefined) return;

    if (list.length === 0) {
      if (selectedSiteId) setSelectedSiteId(null);
      return;
    }

    const allowed = new Set(list.map((s) => s.siteId));
    if (!selectedSiteId || !allowed.has(selectedSiteId)) {
      const preferred = list.find((s) => s.isPreferred) ?? list.at(0);
      if (!preferred) return;
      setSelectedSiteId(preferred.siteId);
    }
  }, [sites.data, selectedSiteId, setSelectedSiteId]);

  const phases = catalog.data?.phases ?? [];

  /**
   * Sélection de phase synchronisée dans `useSelectedPhaseStore` (utilisé aussi par Layout
   * pour piloter les étoiles de la sidebar).
   * - Quand le site change, on reset le store (retombe sur le `userPhaseCode` du nouveau site).
   * - Au clic sur une phase, on met à jour le store directement.
   */
  const selectedPhaseCode: string | null =
    selectedPhaseCodeFromStore ?? progress.data?.userPhaseCode ?? null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: réinitialiser la phase au changement de site ; conflit deps (siteId requis, setter stable).
  useEffect(() => {
    setSelectedPhaseCode(null);
  }, [selectedSiteId, setSelectedPhaseCode]);

  /** Synchroniser le store avec la phase active dès qu'on la connaît (sans écraser un clic). */
  useEffect(() => {
    if (!selectedPhaseCodeFromStore && progress.data?.userPhaseCode) {
      setSelectedPhaseCode(progress.data.userPhaseCode as PhaseCode);
    }
  }, [progress.data?.userPhaseCode, selectedPhaseCodeFromStore, setSelectedPhaseCode]);

  const handleSelectPhase = (code: string) => {
    if (!selectedSiteId) return;
    setSelectedPhaseCode(code as PhaseCode);
  };

  const phaseOrderByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of phases) map.set(p.code, p.order);
    return (code: string) => map.get(code) ?? 0;
  }, [phases]);

  const advanceUser = useAdvanceUser(selectedSiteId ?? '');

  const handleAdvanceUser = () => {
    if (!progress.data) return;
    const currentOrder = phaseOrderByCode(progress.data.userPhaseCode);
    const nextPhase = phases.find((p) => p.order === currentOrder + 1);
    advanceUser.mutate(undefined, {
      onSuccess: () => {
        if (nextPhase) setSelectedPhaseCode(nextPhase.code as PhaseCode);
      },
    });
  };

  const isLoading = catalog.isLoading || sites.isLoading || progress.isLoading;

  if (sites.data && sites.data.length === 0) {
    return (
      <div className="app-card max-w-2xl px-8 py-10 ring-1 ring-slate-200/90">
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">Aucun site assigné</h2>
        <p className="mt-3 text-[0.92rem] leading-relaxed text-slate-600">
          Demandez à un administrateur de vous rattacher à un site pour commencer votre parcours.
        </p>
      </div>
    );
  }

  if (isLoading || !progress.data || !selectedPhaseCode) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={`skeleton-${i}-${Date.now()}`}
              className="h-28 animate-pulse rounded-2xl bg-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }

  const phase = phases.find((p) => p.code === selectedPhaseCode);

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-emerald-800/95">
            Parcours de certification
          </p>
          <h2 className="mt-3 text-[1.875rem] font-semibold tracking-tight text-slate-900 sm:text-[2.1rem]">
            {progress.data.site.name}
          </h2>
          <p className="mt-2 text-[0.95rem] leading-relaxed text-slate-600">
            Site en {labelForPhase(phases, progress.data.sitePhaseCode)}. Mon parcours :{' '}
            {labelForPhase(phases, progress.data.userPhaseCode)}.
          </p>
        </div>
      </header>

      <PhaseTimeline
        catalog={phases}
        progress={progress.data}
        onSelect={handleSelectPhase}
        selectedPhaseCode={selectedPhaseCode}
      />

      {phase && (
        <section className="app-card px-8 py-8 ring-1 ring-slate-200/90">
          <PhaseTaskList
            phase={phase}
            progress={progress.data}
            catalogPhaseOrder={phaseOrderByCode}
            onAdvanceUser={handleAdvanceUser}
          />
        </section>
      )}
    </div>
  );
}

function labelForPhase(phases: Array<{ code: string; label: string }>, code: string): string {
  return phases.find((p) => p.code === code)?.label ?? code;
}
